import React, { useState, useEffect } from 'react';
import { Link as ScrollLink, Element } from 'react-scroll';
import { Link, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { FaLinkedin, FaInstagram, FaTwitter, FaDiscord, FaYoutube } from 'react-icons/fa';
import { CommunityDatasets } from './CommunityDatasets';
// import StepsSection from './StepsSection';
import PricingSection from './PricingSection';
import VisionMissionSection from './VisionMissionSection';
import StatsSection from './StatsSection';
import FAQSection from './FAQSection';
import CommunitySection from './CommunitySection';
import ReviewWorkflowDemo from './ReviewWorkflowDemo';
import HowItWorksSection from './HowItWorksSection';
import UserReviewsSection from './UserReviewsSection';
import PromoBanner from './PromoBanner';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [activeScenario, setActiveScenario] = useState(0);

  const scenarios = [
    {
      file: "medical_records_v2.json",
      field: "Recommended Dosage",
      label: "Drug Name",
      context: "Paracetamol (Acetaminophen)",
      error: "⚠️ 50.0g",
      correction: "✓ 500mg",
      note: "Corrected fatal dosage error. 50g is toxic; standard dose is 500mg-1g."
    },
    {
      file: "legal_contracts_saas.json",
      field: "Notice Period",
      label: "Clause Type",
      context: "Termination for Convenience",
      error: "⚠️ 24 hours",
      correction: "✓ 30 days",
      note: "Standardized termination notice period to compliant 30-day term."
    },
    {
      file: "finance_fraud_logs.csv",
      field: "Transaction Category",
      label: "Merchant info",
      context: "Netflix.com Subscription",
      error: "⚠️ Utilities",
      correction: "✓ Entertainment",
      note: "Corrected category misclassification for recurring subscription."
    },
    {
      file: "python_instruct_tuning.jsonl",
      field: "Code Security",
      label: "Input Snippet",
      context: "eval(user_input)",
      error: "⚠️ Safe",
      correction: "✓ Unsafe",
      note: "Flagged 'eval()' usage as high-severity security vulnerability."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveScenario((prev) => (prev + 1) % scenarios.length);
    }, 6000); // Match animation cycle
    return () => clearInterval(interval);
  }, []);


  const getAnimationProps = (index) => {
    // First two cards come from left, last two from right
    const isFromLeft = index < 2;
    return {
      initial: {
        opacity: 0,
        x: isFromLeft ? -100 : 100
      },
      animate: {
        opacity: 1,
        x: 0
      },
      transition: {
        duration: 0.5,
        delay: index * 0.1
      }
    };
  };

  /* Simplified Navigation - Flattened */
  const navigationItems = [
    { name: "features", title: "Features", isScroll: true },
    { name: "how-it-works", title: "How it Works", isScroll: true },
    { name: "pricing", title: "Pricing", isScroll: true },
    { name: "datasets", title: "Datasets", isScroll: true },
    { name: "reviewer", title: "Experts", isScroll: true },
    { name: "blog", title: "Blog", isScroll: false },
    { name: "community", title: "Community", isScroll: true },
    { name: "faq", title: "FAQ", isScroll: true },
  ];

  const features = [
    {
      title: "Multiple Task Support",
      description: "Generate high-quality synthetic datasets for both numeric and text-based data. Support various use cases, including Text Classification, Instruction Fine-Tuning, and structured numerical datasets.",
      link: "/blog/synthetic-data-nlp"
    },
    {
      title: "Instant Data Generation",
      description: "Watch an instant preview of your generated dataset before finalizing. Quickly assess structure, quality, and relevance in real time, ensuring the data meets your needs before downloading.",
      link: "/blog/synthetic-data-ai"
    },
    {
      title: "Augment Datasets",
      description: "Upload your own custom knowledge or data sources and generate data that reflects the style and knowledge of the existing data. Integrate external knowledge sources ensuring domain relevance.",
      link: "/blog/synthetic-data-ai"
    },
    {
      title: "Data Cleaning",
      description: "Clean and preprocess your datasets with AI-powered tools. Remove duplicates, fix inconsistencies, and ensure data quality before training your models.",
      link: "/blog/synthetic-data-ai"
    }
  ];

  const handleNavigation = (sectionName) => {
    if (sectionName === "blog") {
      navigate("/blog");
    } else if (sectionName === "pricing") {
      navigate("/pricing");
    } else if (sectionName === "login") {
      navigate("/signin");
    }
  };

  const handleGetStarted = () => {
    navigate("/signin");
  };

  const handleJoinEvent = () => {
    // navigate("/blog");
    // window.location.href = "https://datacreatorai.com/pricing" 
    window.open("https://forms.gle/6swkJt4HUxvtv6LQA", "_blank");
    // "https://discord.gg/pmfEZWkH";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="relative">
        <header className="relative w-full z-50 bg-white shadow-md">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <RouterLink to="/" style={{ textDecoration: 'none', flexGrow: 1 }}>
                  <span className="text-2xl font-bold text-blue-600">DC</span>
                </RouterLink>
                <span className="hidden sm:inline-block ml-2 text-xl font-semibold text-gray-800">
                  DataCreator AI<sup className="text-xs text-blue-600">Beta</sup>
                </span>
              </div>
              <div className="flex items-center space-x-8">
                <nav className="hidden md:flex space-x-6">
                  {navigationItems.map((item) => (
                    item.isScroll ? (
                      <ScrollLink
                        key={item.name}
                        to={item.name}
                        smooth={true}
                        duration={500}
                        offset={-70}
                        className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium cursor-pointer"
                      >
                        {item.title}
                      </ScrollLink>
                    ) : (
                      <button
                        key={item.name}
                        onClick={() => handleNavigation(item.name)}
                        className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium"
                      >
                        {item.title}
                      </button>
                    )
                  ))}
                </nav>
                <button
                  onClick={() => handleNavigation("login")}
                  className="hidden md:block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Sign up
                </button>
              </div>
              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-gray-500 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                >
                  {isMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>
        <PromoBanner />
      </div>

      {/* Mobile menu */}
      <motion.div
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : -100 }}
        transition={{ duration: 0.3 }}
        className={`md:hidden fixed inset-0 z-50 bg-white ${isMenuOpen ? 'block' : 'hidden'}`}
      >
        <div className="pt-20 pb-4 px-4">
          <div className="flex flex-col space-y-4">
            {navigationItems.map((item) => (
              item.isScroll ? (
                <ScrollLink
                  key={item.name}
                  to={item.name}
                  smooth={true}
                  duration={500}
                  offset={-70}
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium"
                >
                  {item.title}
                </ScrollLink>
              ) : (
                <button
                  key={item.name}
                  onClick={() => {
                    handleNavigation(item.name);
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium"
                >
                  {item.title}
                </button>
              )
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => {
                handleNavigation("login");
                setIsMenuOpen(false);
              }}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300"
            >
              Sign up
            </button>
          </div>
        </div>
      </motion.div>

      {/* Hero Section */}
      <Element name="hero" className="pt-20 md:pt-16 pb-16 md:pb-24 bg-white relative overflow-hidden">
        {/* Dotted background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #000000 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }}
        />
        {/* Animated wave effect */}
        <div
          className="absolute inset-0 opacity-15"
          style={{
            background: 'radial-gradient(ellipse 800px 200px at 50% 50%, rgba(37, 99, 235, 0.15) 0%, transparent 70%)',
            backgroundSize: '100% 100%',
            animation: 'waveMove 6s ease-in-out infinite'
          }}
        />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            background: 'radial-gradient(ellipse 600px 150px at 50% 50%, rgba(37, 99, 235, 0.1) 0%, transparent 70%)',
            backgroundSize: '100% 100%',
            animation: 'waveMove 8s ease-in-out infinite reverse'
          }}
        />
        <style>{`
          @keyframes waveMove {
            0%, 100% {
              transform: translateX(-100%) scaleY(1);
              opacity: 0.1;
            }
            50% {
              transform: translateX(100%) scaleY(1.2);
              opacity: 0.2;
            }
          }
        `}</style>
        {/* <div className="flex justify-center gap-4 sm:px-6 lg:px-8">
          <a href="https://fazier.com" target="_blank" rel="noopener noreferrer">
            <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=light" width="120" alt="Fazier badge" />
          </a>
          <a href="https://solopush.com/product/datacreator-ai" target="_blank" rel="noopener noreferrer">
            <img src="https://firebasestorage.googleapis.com/v0/b/datagenerator-425908.firebasestorage.app/o/SoloPush%231.jpg?alt=media&token=2fd677d6-2d8b-42ba-bd92-39bf8fdd63aa" width="120" alt="Solopush badge" />
          </a>
        </div> */}
        {/* <div className="flex justify-center items-center gap-6 sm:px-6 lg:px-8 relative z-10">
            <a href="https://fazier.com" target="_blank" rel="noopener noreferrer">
              <img
                src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=light"
                width="120"
                alt="Fazier badge"
              />
            </a>
            <a href="https://solopush.com/product/datacreator-ai" target="_blank" rel="noopener noreferrer">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/datagenerator-425908.firebasestorage.app/o/SoloPush%231.jpg?alt=media&token=2fd677d6-2d8b-42ba-bd92-39bf8fdd63aa"  // replace with the actual image URL
                alt="Solopush badge"
                className="h-12 sm:h-10 object-contain"
              />
            </a>
            <a href="https://www.uneed.best/tool/datacreator-ai">
              <img src="https://www.uneed.best/EMBED3.png" alt="Uneed Embed Badge"  className="h-12 sm:h-10 object-contain" />
            </a>
        </div> */}


        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-3 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-2xl text-blue-600 font-bold mb-4"
            >
              Infinite Data, Limitless Possibilities
            </motion.h2>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-blue-600 mb-8 leading-tight"
            >
              Never Run Out of Data.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-xl text-blue-600 mb-12 max-w-3xl mx-auto"
            >
              Create, clean, label, and augment datasets with AI-generated data. Eliminate manual data prep and get model-ready datasets in minutes.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <button
                onClick={handleGetStarted}
                className="bg-white-600 text-blue-600 font-bold py-3 px-8 rounded-full border-2 border-blue-600 hover:bg-white-700 transition-colors duration-300 shadow-lg"
              >
                GET STARTED FOR FREE
              </button>
              <button
                onClick={handleJoinEvent}
                className="bg-blue-600 text-white font-bold py-3 px-8 rounded-full border-2 border-blue-600 hover:bg-white-50 transition-colors duration-300 shadow-lg"
              >
                DOWNLOAD OUR SDK
              </button>
            </motion.div>
          </div>
        </div>
      </Element>

      {/* Student Ambassador Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 border-y border-blue-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium mb-3">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                Now Accepting Applications
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Join Our Student Ambassador Program
              </h2>
              <p className="text-blue-100 text-lg">
                Get free access, mentorship, and earn commission. Applications close Feb 28, 2026.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/student-ambassadors')}
                className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors duration-200 whitespace-nowrap"
              >
                Learn More
              </button>
              <a
                href="https://forms.gle/adwyG7GxmVGks7Wi7"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-blue-800 text-white font-semibold rounded-lg hover:bg-blue-900 transition-colors duration-200 whitespace-nowrap border-2 border-white/20"
              >
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <Element name="features" className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-blue-600 mb-12">
            Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                {...getAnimationProps(index)}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <h3 className="text-xl font-semibold text-blue-600 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4 flex-grow">
                  {feature.description}
                </p>
                <RouterLink
                  to={feature.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-300 group"
                >
                  Read More
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </RouterLink>
              </motion.div>
            ))}
          </div>
        </div>
      </Element>


      <Element name="how-it-works" className="bg-white">
        <HowItWorksSection />
      </Element>

      {/* Use Cases Section */}
      <Element name="usecases" className="py-20 bg-white relative overflow-hidden">
        {/* Subtle background pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle, #3B82F6 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-blue-600 mb-4">
              Powering AI Across Industries
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Generate high-quality synthetic text data tailored for your specific domain
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Healthcare Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-100 overflow-hidden"
            >
              {/* Dotted background pattern */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(circle, #2563EB 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-3">Healthcare</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Train medical AI without privacy concerns. Generate synthetic patient discharge summaries, doctor-patient dialogues, and symptom descriptions.
                </p>
                <div className="bg-white/80 rounded-lg p-3 mb-4 border border-blue-100">
                  <p className="text-sm text-gray-700 italic">"Generate 10,000 synthetic patient discharge summaries for training a hospital chatbot"</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Discharge Notes</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Symptom QA</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Medical Dialogues</span>
                </div>
              </div>
            </motion.div>

            {/* Finance Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-100 overflow-hidden"
            >
              {/* Dotted background pattern */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(circle, #2563EB 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-3">Finance</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Build fraud detection and customer service AI. Create synthetic transaction narratives, loan application texts, and financial advisory conversations.
                </p>
                <div className="bg-white/80 rounded-lg p-3 mb-4 border border-blue-100">
                  <p className="text-sm text-gray-700 italic">"Create 5,000 synthetic fraud vs legitimate transaction descriptions for classification"</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Fraud Detection</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Loan Reviews</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Advisory Chats</span>
                </div>
              </div>
            </motion.div>

            {/* AI Agent Development Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-100 overflow-hidden"
            >
              {/* Dotted background pattern */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(circle, #2563EB 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-3">AI Agent Development</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Fine-tune conversational AI agents with realistic multi-turn dialogues, tool-calling examples, and complex reasoning chains.
                </p>
                <div className="bg-white/80 rounded-lg p-3 mb-4 border border-blue-100">
                  <p className="text-sm text-gray-700 italic">"Generate 2,000 multi-turn customer support conversations with tool-use annotations"</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Tool Calling</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Multi-turn</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Reasoning</span>
                </div>
              </div>
            </motion.div>

            {/* Edge Case Coverage Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-100 overflow-hidden"
            >
              {/* Dotted background pattern */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(circle, #2563EB 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-3">Edge Case Coverage</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Stress-test your models with rare scenarios and adversarial inputs. Generate edge cases your production data doesn't cover.
                </p>
                <div className="bg-white/80 rounded-lg p-3 mb-4 border border-blue-100">
                  <p className="text-sm text-gray-700 italic">"Create 500 adversarial product reviews with subtle sentiment contradictions"</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Rare Scenarios</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Adversarial</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Stress Testing</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Element>

      <Element name="stats" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <StatsSection />
      </Element>

      {/* User Reviews Section */}
      <Element name="reviews">
        <UserReviewsSection />
      </Element>

      <Element name="workflow" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <ReviewWorkflowDemo />
      </Element>

      <Element name="pricing" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <PricingSection />
      </Element>

      <Element name="datasets" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <CommunityDatasets />
      </Element>

      <Element name="vision" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <VisionMissionSection />
      </Element>

      <Element name="reviewer" className="py-20 bg-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-blue-600 skew-y-3 transform origin-bottom-right -z-10 translate-y-20"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
            <div className="flex flex-col md:flex-row items-center">
              <div className="w-full md:w-1/2 p-10 md:p-16">
                <span className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mb-6">
                  Expert Network
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Are you a Domain Expert? <br />
                  <span className="text-blue-600">Earn by Reviewing Data.</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Join our elite network of data reviewers. We need experts in Medicine, Law, Finance, and STEM to verify synthetic datasets. Ensure privacy-safe AI is accurate and reliable.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/reviewer-apply')}
                    className="inline-flex items-center justify-center px-8 py-3.5 text-base font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-1"
                  >
                    Apply as Reviewer
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                  {/* <button className="inline-flex items-center justify-center px-8 py-3.5 text-base font-bold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all border border-gray-200">
                    Learn More
                  </button> */}
                </div>
              </div>
              <div className="w-full md:w-1/2 bg-gray-50 p-10 flex items-center justify-center min-h-[400px]">
                <div className="relative w-full max-w-sm">
                  {/* Data Correction Animation */}
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative h-[340px]">
                    <AnimatePresence mode='wait'>
                      <motion.div
                        key={activeScenario}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="h-full flex flex-col"
                      >
                        {/* Header */}
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-red-400"></span>
                            <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                            <span className="w-3 h-3 rounded-full bg-green-400"></span>
                          </div>
                          <div className="text-xs font-mono text-gray-400">{scenarios[activeScenario].file}</div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6 flex-grow relative">
                          {/* Context Field */}
                          <div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{scenarios[activeScenario].label}</div>
                            <div className="text-gray-900 font-medium">{scenarios[activeScenario].context}</div>
                          </div>

                          {/* Moving/Correcting Field */}
                          <div className="relative">
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{scenarios[activeScenario].field}</div>
                            <div className="flex items-center gap-3">
                              <motion.div
                                initial={{ opacity: 1, display: "block" }}
                                animate={{
                                  opacity: [1, 1, 0, 0, 0],
                                  display: ["block", "block", "none", "none", "none"]
                                }}
                                transition={{ duration: 6, times: [0, 0.4, 0.45, 0.9, 1], repeat: Infinity }}
                                className="text-xl font-mono text-red-500 bg-red-50 px-2 py-1 rounded strike-through flex items-center gap-2"
                              >
                                {scenarios[activeScenario].error}
                              </motion.div>

                              <motion.div
                                initial={{ opacity: 0, display: "none" }}
                                animate={{
                                  opacity: [0, 0, 1, 1, 0],
                                  display: ["none", "none", "block", "block", "none"]
                                }}
                                transition={{ duration: 6, times: [0, 0.45, 0.5, 0.9, 1], repeat: Infinity }}
                                className="text-xl font-mono text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-2"
                              >
                                {scenarios[activeScenario].correction}
                              </motion.div>
                            </div>

                            {/* Cursor Animation */}
                            <motion.div
                              animate={{
                                x: [0, 120, 120, 0],
                                y: [40, 40, -10, -10],
                                opacity: [0, 1, 1, 0]
                              }}
                              transition={{ duration: 6, times: [0, 0.2, 0.4, 0.6], repeat: Infinity }}
                              className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                            >
                              <svg className="w-6 h-6 text-gray-800 drop-shadow-md" fill="currentColor" viewBox="0 0 24 24"><path d="M7 2l12 11.2-5.8.5 3.3 7.3-2.2.9-3.2-7.4-4.4 4z" /></svg>
                            </motion.div>
                          </div>

                          {/* Reviewer Comment */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: [0, 0, 1, 1, 0] }}
                            transition={{ duration: 6, times: [0, 0.5, 0.55, 0.9, 1], repeat: Infinity }}
                            className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3 absolute bottom-6 left-6 right-6"
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-200 flex-shrink-0 flex items-center justify-center text-xs">👨‍💻</div>
                            <div>
                              <div className="text-xs font-bold text-blue-800">Reviewer Note</div>
                              <div className="text-xs text-blue-700">{scenarios[activeScenario].note}</div>
                            </div>
                          </motion.div>
                        </div>

                        {/* Status Bar */}
                        <motion.div
                          animate={{
                            backgroundColor: ["#fee2e2", "#fee2e2", "#dcfce7", "#dcfce7", "#fee2e2"]
                          }}
                          transition={{ duration: 6, times: [0, 0.45, 0.5, 0.9, 1], repeat: Infinity }}
                          className="h-2 w-full mt-auto"
                        />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Element>

      {/* Add Community Section */}
      <Element name="community" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <CommunitySection />
      </Element>

      {/* Add FAQ Section */}
      <Element name="faq" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <FAQSection />
      </Element>

      {/* CTA Section */}
      <Element name="cta" className="bg-blue-600 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to create your own dataset?</h2>
            <p className="text-xl text-blue-100 mb-8">Start generating custom synthetic data today.</p>
            <button
              onClick={handleGetStarted}
              className="bg-white text-blue-600 font-bold py-3 px-8 rounded-full hover:bg-blue-50 transition-colors duration-300 shadow-lg"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </Element>

      {/* Footer */}
      <footer className="bg-gray-100 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="text-gray-600">
                &copy; {new Date().getFullYear()} DataCreator AI. All rights reserved.
              </div>
              {/* <a href="https://fazier.com" target="_blank" rel="noopener noreferrer">
                <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=light" width={120} alt="Fazier badge" />
              </a> */}
            </div>
            {/* <div className="flex space-x-4">
              <a href="https://discord.gg/b9yXWc6K" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors duration-300">
                <FaDiscord size={24} />
              </a>
              <a href="https://www.linkedin.com/company/syntheta/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors duration-300">
                <FaLinkedin size={24} />
              </a>
              <a href="https://www.youtube.com/@aiportal481" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors duration-300">
                <FaYoutube size={24} />
              </a>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;